/**
 * Lazy Ajv validator (EID-63).
 *
 * The eidos root schema $refs the full vega-lite schema (~1.87 MB), and
 * constructing the Ajv instance over the whole schema set is expensive.
 * Doing that at module scope dragged the schemas + Ajv into the eager entry
 * chunk of both apps. This module defers all of it: the schema JSONs and Ajv
 * are dynamic-imported and the validator is compiled on first use, then
 * memoised. Callers `await getSchemaValidator()` instead of importing a
 * module-scope `schemaValidator`.
 */
import type AjvType from 'ajv';

let validatorPromise: Promise<AjvType> | undefined;

const buildValidator = async (): Promise<AjvType> => {
  const [
    { default: Ajv },
    { default: addFormats },
    data_schema,
    oceanql_schema,
    plot_schema,
    world_schema,
    vega_schema,
    document_schema,
    grid_schema,
    menu_schema,
    derived_schema,
    eidos_schema,
    common_schema,
    panel_schema,
    theme_schema,
    geojson_schema,
    track_schema,
    feature_schema,
    gridded_schema,
    label_schema,
    scenegraph_schema,
    pointcloud_schema,
    seasurface_schema,
    wmts_schema,
    layer_schema,
  ] = await Promise.all([
    import('ajv'),
    import('ajv-formats'),
    import('./src/eidos/data.json'),
    import('./src/datamesh/oceanql.json'),
    import('./src/eidos/node/plot.json'),
    import('./src/eidos/node/world.json'),
    import('./src/vega.json'),
    import('./src/eidos/node/document.json'),
    import('./src/eidos/node/grid.json'),
    import('./src/eidos/node/menu.json'),
    import('./src/eidos/node/derived.json'),
    import('./src/eidos/root.json'),
    import('./src/eidos/common.json'),
    import('./src/eidos/panel.json'),
    import('./src/eidos/theme.json'),
    import('./src/geojson.json'),
    import('./src/eidos/node/worldlayer/track.json'),
    import('./src/eidos/node/worldlayer/feature.json'),
    import('./src/eidos/node/worldlayer/gridded.json'),
    import('./src/eidos/node/worldlayer/label.json'),
    import('./src/eidos/node/worldlayer/scenegraph.json'),
    import('./src/eidos/node/worldlayer/pointcloud.json'),
    import('./src/eidos/node/worldlayer/seasurface.json'),
    import('./src/eidos/node/worldlayer/wmts.json'),
    import('./src/eidos/node/worldlayer/common.json'),
  ]);

  const unwrap = (m: unknown) =>
    (m as { default?: unknown }).default ?? (m as unknown);

  const schemaValidator = addFormats(
    new Ajv({
      schemas: [
        eidos_schema,
        data_schema,
        oceanql_schema,
        plot_schema,
        world_schema,
        document_schema,
        vega_schema,
        common_schema,
        panel_schema,
        grid_schema,
        menu_schema,
        derived_schema,
        theme_schema,
        geojson_schema,
        track_schema,
        feature_schema,
        gridded_schema,
        label_schema,
        scenegraph_schema,
        pointcloud_schema,
        seasurface_schema,
        wmts_schema,
        layer_schema,
      ].map(unwrap) as object[],
      strict: false,
    }),
  );

  schemaValidator.addKeyword('dataType', {
    keyword: 'dataType',
    validate: function (schema: unknown, data: unknown) {
      switch (schema) {
        case 'bigint':
          return typeof data === 'bigint';

        // A JS TypedArray (Float64Array, BigInt64Array, ...) — what
        // DataVar.get() returns for numeric zarr columns. DataView is also an
        // ArrayBuffer view but is not array-like, so it is excluded.
        case 'typedarray':
          return ArrayBuffer.isView(data) && !(data instanceof DataView);

        default:
          return schemaValidator.compile({ type: schema })(data);
      }
    },
    errors: true,
  });

  return schemaValidator;
};

/**
 * Returns the shared Ajv validator, compiling it (and loading the schema
 * JSONs + Ajv) on first call. The promise is memoised so the work happens
 * at most once.
 */
export const getSchemaValidator = (): Promise<AjvType> => {
  if (!validatorPromise) validatorPromise = buildValidator();
  return validatorPromise;
};
